import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addToast } from '@heroui/react';
import { TeacherRequestReason, useCreateTeacherRequestMutation } from '@/entities/TeacherRequests';
import { AppButton } from '@/shared/ui/AppButton';
import { DateSelectField, SelectField, TextareaField } from '@/shared/ui/Fields';
import { ProfileRequestShell } from '../components/ProfileRequestShell';
import { RequestFilesField } from '../components/RequestFilesField';
import { TEACHER_REQUEST_REASON_OPTIONS } from '../../components/TeacherRequests/teacherRequestUi';

const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message);
  }

  return 'Failed to create request';
};

export const TeacherCreateRequestView = () => {
  const navigate = useNavigate();
  const [reason, setReason] = useState<TeacherRequestReason | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [createTeacherRequest, { isLoading }] = useCreateTeacherRequestMutation();

  const canSubmit = Boolean(reason && comment.trim());

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      const request = await createTeacherRequest({
        reason: reason as TeacherRequestReason,
        staffComment: comment.trim(),
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        staffAttachments: files.length ? files : undefined,
      }).unwrap();

      addToast({
        title: 'Request created',
        description: 'We’ve received your request and will review it shortly.',
        color: 'success',
      });
      navigate(`/profile/teacher/requests/${request.id}`);
    } catch (error) {
      addToast({ title: getErrorMessage(error), color: 'danger' });
    }
  };

  return (
    <ProfileRequestShell title="Create request">
      <div className="flex w-[520px] flex-col gap-5">
        <SelectField
          name="reason"
          label="Reason"
          placeholder="Select reason"
          value={reason}
          options={TEACHER_REQUEST_REASON_OPTIONS}
          onChange={(value) => setReason(value as TeacherRequestReason)}
        />
        <div className="grid grid-cols-2 gap-5">
          <DateSelectField
            name="startDate"
            label="Period start (optional)"
            value={startDate}
            dateFormat="dd/MM/yyyy"
            onChange={(_, value) => setStartDate(value)}
          />
          <DateSelectField
            name="endDate"
            label="Period end (optional)"
            value={endDate}
            minDate={startDate ? new Date(startDate) : undefined}
            dateFormat="dd/MM/yyyy"
            onChange={(_, value) => setEndDate(value)}
          />
        </div>
        <TextareaField
          name="comment"
          label="Comment"
          placeholder="Enter your comment"
          rows={5}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
        <RequestFilesField files={files} onChange={setFiles} />
        <div className="mt-4 grid grid-cols-2 gap-6">
          <AppButton color="white" height={56} onPress={() => navigate('/profile')}>
            Cancel
          </AppButton>
          <AppButton color="red" height={56} disabled={!canSubmit} isLoading={isLoading} onPress={handleSubmit}>
            Submit
          </AppButton>
        </div>
      </div>
    </ProfileRequestShell>
  );
};
